import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { publicShareApi } from '../../services/fileShareApi';
import { format } from 'date-fns';

const PublicShareListDialog = ({ open, onClose, file }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    if (open && file) {
      loadShares();
    }
  }, [open, file]);

  const loadShares = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await publicShareApi.getSharesForFile(file.id || file.fileId);

      if (response.data.success) {
        setShares(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load shares');
      }
    } catch (err) {
      console.error('Error loading shares:', err);
      setError(err.response?.data?.message || 'Failed to load shares');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (shareId) => {
    if (!window.confirm('Are you sure you want to revoke this share link?')) {
      return;
    }

    try {
      const response = await publicShareApi.revokeShareLink(shareId);

      if (response.data.success) {
        setCopySuccess('Share link revoked successfully');
        loadShares(); // Reload list
      } else {
        setError(response.data.message || 'Failed to revoke share link');
      }
    } catch (err) {
      console.error('Error revoking share:', err);
      setError(err.response?.data?.message || 'Failed to revoke share link');
    }
  };

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(`${type} copied to clipboard!`);
  };

  const getStatusChip = (share) => {
    if (share.isRevoked) {
      return <Chip label="Revoked" color="error" size="small" />;
    }
    if (!share.isValid) {
      return <Chip label="Expired" color="warning" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const formatExpiryDuration = (duration) => {
    switch (duration) {
      case '1h':
        return '1 Hour';
      case '6h':
        return '6 Hours';
      case '24h':
        return '24 Hours';
      case '7d':
        return '7 Days';
      case '30d':
        return '30 Days';
      case 'never':
        return 'No expiry';
      case 'custom':
        return 'Custom';
      default:
        return duration || '—';
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Public Share Links</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : shares.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                No public shares found for this file.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Link</TableCell>
                    <TableCell>Expiry option</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="center">Views</TableCell>
                    <TableCell>Last Accessed</TableCell>
                    <TableCell>Download</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shares.map((share) => {
                    const shareUrl = `${window.location.origin}${share.shareUrl}`;

                    return (
                      <TableRow key={share.shareId}>
                        <TableCell>{getStatusChip(share)}</TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {shareUrl}
                            </Typography>
                            <Tooltip title="Copy link">
                              <IconButton
                                size="small"
                                onClick={() => handleCopy(shareUrl, 'Link')}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {formatExpiryDuration(share.expiryDuration)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(share.expiresAt)}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {share.viewCount}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {share.lastAccessedAt ? formatDate(share.lastAccessedAt) : 'Never'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={share.allowDownload ? 'Yes' : 'No'}
                            size="small"
                            color={share.allowDownload ? 'primary' : 'default'}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="View access logs">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  // TODO: Open access logs dialog
                                  alert('Access logs feature coming soon');
                                }}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Revoke link">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRevoke(share.shareId)}
                                disabled={share.isRevoked}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button variant="outlined" onClick={loadShares}>
            Refresh
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={!!copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess('')}
        message={copySuccess}
      />
    </>
  );
};

export default PublicShareListDialog;

